import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  ApplicationDecisionDto,
  BrandDealAction,
  BrandDealDto,
  CampaignDto,
  CreatorApplicationDecisionType,
  CreatorApplicationDto,
  CreatorCampaignAction,
  CreatorInvitationAction,
  CreatorMembershipAction,
  CreatorParticipationAction,
  CreatorPaymentAction,
  CreatorPaymentDto,
  CreatorProfileAction,
  CreatorProfileDto,
  CreatorProgramAction,
  CreatorProgramDto,
  CreatorTierAction,
  InvitationDto,
  ListParams,
  MembershipDto,
  PaginationMeta,
  ParticipationDto,
  PerformanceDto,
  RequestMeta,
  ReviewNoteDto,
  TierHistoryDto,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/creators`;

function toQuery(params: ListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createCreatorsApi(client: ApiClient) {
  return {
    listProfiles(params: ListParams = {}) {
      return client.get<{
        creator_profiles: CreatorProfileDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/profiles${toQuery(params)}`);
    },
    getProfile(id: string) {
      return client.get<{ creator_profile: CreatorProfileDto; meta: RequestMeta }>(
        `${BASE}/profiles/${id}`,
      );
    },
    createProfile(body: Record<string, unknown>) {
      return client.post<{ creator_profile: CreatorProfileDto; meta: RequestMeta }>(
        `${BASE}/profiles`,
        body,
        newIdempotencyKey(),
      );
    },
    patchProfile(id: string, body: Record<string, unknown>) {
      return client.patch<{ creator_profile: CreatorProfileDto; meta: RequestMeta }>(
        `${BASE}/profiles/${id}`,
        body,
      );
    },
    applyProfileAction(id: string, action: CreatorProfileAction, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/profiles/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
    attachProfileFile(id: string, body: Record<string, unknown>) {
      return client.post(`${BASE}/profiles/${id}/files`, body, newIdempotencyKey());
    },

    listApplications(params: ListParams = {}) {
      return client.get<{
        creator_applications: CreatorApplicationDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/applications${toQuery(params)}`);
    },
    getApplication(id: string) {
      return client.get<{
        creator_application: CreatorApplicationDto;
        meta: RequestMeta;
      }>(`${BASE}/applications/${id}`);
    },
    createApplication(body: Record<string, unknown>) {
      return client.post<{
        creator_application: CreatorApplicationDto;
        meta: RequestMeta;
      }>(`${BASE}/applications`, body, newIdempotencyKey());
    },
    submitApplication(id: string, body: Record<string, unknown> = {}) {
      return client.post(`${BASE}/applications/${id}/submit`, body, newIdempotencyKey());
    },
    startReview(id: string, body: Record<string, unknown> = {}) {
      return client.post(
        `${BASE}/applications/${id}/start_review`,
        body,
        newIdempotencyKey(),
      );
    },
    decideApplication(
      id: string,
      body: {
        decision: CreatorApplicationDecisionType;
        decision_reason?: string;
        reason?: string;
        approval_id?: string;
      },
    ) {
      return client.post(`${BASE}/applications/${id}/decide`, body, newIdempotencyKey());
    },
    resumeApplication(id: string, body: Record<string, unknown> = {}) {
      return client.post(`${BASE}/applications/${id}/resume`, body, newIdempotencyKey());
    },
    closeApplication(id: string, body: Record<string, unknown> = {}) {
      return client.post(`${BASE}/applications/${id}/close`, body, newIdempotencyKey());
    },
    listReviewNotes(id: string) {
      return client.get<{ review_notes: ReviewNoteDto[]; meta: RequestMeta }>(
        `${BASE}/applications/${id}/notes`,
      );
    },
    addReviewNote(id: string, body: Record<string, unknown>) {
      return client.post<{ review_note: ReviewNoteDto; meta: RequestMeta }>(
        `${BASE}/applications/${id}/notes`,
        body,
        newIdempotencyKey(),
      );
    },
    listDecisions(id: string) {
      return client.get<{ decisions: ApplicationDecisionDto[]; meta: RequestMeta }>(
        `${BASE}/applications/${id}/decisions`,
      );
    },
    attachApplicationFile(id: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/applications/${id}/files`,
        body,
        newIdempotencyKey(),
      );
    },

    listTierHistory(params: ListParams = {}) {
      return client.get<{
        tier_history: TierHistoryDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/tier-history${toQuery(params)}`);
    },
    createTierHistory(body: Record<string, unknown>) {
      return client.post<{ tier_history: TierHistoryDto; meta: RequestMeta }>(
        `${BASE}/tier-history`,
        body,
        newIdempotencyKey(),
      );
    },
    applyTierAction(id: string, action: CreatorTierAction, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/tier-history/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listPrograms(params: ListParams = {}) {
      return client.get<{
        creator_programs: CreatorProgramDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/programs${toQuery(params)}`);
    },
    getProgram(id: string) {
      return client.get<{ creator_program: CreatorProgramDto; meta: RequestMeta }>(
        `${BASE}/programs/${id}`,
      );
    },
    createProgram(body: Record<string, unknown>) {
      return client.post<{ creator_program: CreatorProgramDto; meta: RequestMeta }>(
        `${BASE}/programs`,
        body,
        newIdempotencyKey(),
      );
    },
    patchProgram(id: string, body: Record<string, unknown>) {
      return client.patch<{ creator_program: CreatorProgramDto; meta: RequestMeta }>(
        `${BASE}/programs/${id}`,
        body,
      );
    },
    applyProgramAction(id: string, action: CreatorProgramAction, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/programs/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listMemberships(params: ListParams = {}) {
      return client.get<{
        memberships: MembershipDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/memberships${toQuery(params)}`);
    },
    createMembership(body: Record<string, unknown>) {
      return client.post<{ membership: MembershipDto; meta: RequestMeta }>(
        `${BASE}/memberships`,
        body,
        newIdempotencyKey(),
      );
    },
    applyMembershipAction(
      id: string,
      action: CreatorMembershipAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/memberships/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listCampaigns(params: ListParams = {}) {
      return client.get<{
        campaigns: CampaignDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/campaigns${toQuery(params)}`);
    },
    getCampaign(id: string) {
      return client.get<{ campaign: CampaignDto; meta: RequestMeta }>(
        `${BASE}/campaigns/${id}`,
      );
    },
    createCampaign(body: Record<string, unknown>) {
      return client.post<{ campaign: CampaignDto; meta: RequestMeta }>(
        `${BASE}/campaigns`,
        body,
        newIdempotencyKey(),
      );
    },
    patchCampaign(id: string, body: Record<string, unknown>) {
      return client.patch<{ campaign: CampaignDto; meta: RequestMeta }>(
        `${BASE}/campaigns/${id}`,
        body,
      );
    },
    applyCampaignAction(id: string, action: CreatorCampaignAction, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/campaigns/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listInvitations(params: ListParams = {}) {
      return client.get<{
        invitations: InvitationDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/invitations${toQuery(params)}`);
    },
    createInvitation(body: Record<string, unknown>) {
      return client.post<{ invitation: InvitationDto; meta: RequestMeta }>(
        `${BASE}/invitations`,
        body,
        newIdempotencyKey(),
      );
    },
    applyInvitationAction(
      id: string,
      action: CreatorInvitationAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/invitations/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listParticipations(params: ListParams = {}) {
      return client.get<{
        participations: ParticipationDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/participations${toQuery(params)}`);
    },
    createParticipation(body: Record<string, unknown>) {
      return client.post<{ participation: ParticipationDto; meta: RequestMeta }>(
        `${BASE}/participations`,
        body,
        newIdempotencyKey(),
      );
    },
    applyParticipationAction(
      id: string,
      action: CreatorParticipationAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/participations/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listBrandDeals(params: ListParams = {}) {
      return client.get<{
        brand_deals: BrandDealDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/brand-deals${toQuery(params)}`);
    },
    getBrandDeal(id: string) {
      return client.get<{ brand_deal: BrandDealDto; meta: RequestMeta }>(
        `${BASE}/brand-deals/${id}`,
      );
    },
    createBrandDeal(body: Record<string, unknown>) {
      return client.post<{ brand_deal: BrandDealDto; meta: RequestMeta }>(
        `${BASE}/brand-deals`,
        body,
        newIdempotencyKey(),
      );
    },
    patchBrandDeal(id: string, body: Record<string, unknown>) {
      return client.patch<{ brand_deal: BrandDealDto; meta: RequestMeta }>(
        `${BASE}/brand-deals/${id}`,
        body,
      );
    },
    applyBrandDealAction(id: string, action: BrandDealAction, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/brand-deals/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listPerformance(params: ListParams = {}) {
      return client.get<{
        performance_records: PerformanceDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/performance-records${toQuery(params)}`);
    },
    createPerformance(body: Record<string, unknown>) {
      return client.post<{ performance_record: PerformanceDto; meta: RequestMeta }>(
        `${BASE}/performance-records`,
        body,
        newIdempotencyKey(),
      );
    },
    applyPerformanceAction(id: string, action: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/performance-records/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listPayments(params: ListParams = {}) {
      return client.get<{
        payments: CreatorPaymentDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/payments${toQuery(params)}`);
    },
    createPayment(body: Record<string, unknown>) {
      return client.post<{ payment: CreatorPaymentDto; meta: RequestMeta }>(
        `${BASE}/payments`,
        body,
        newIdempotencyKey(),
      );
    },
    applyPaymentAction(id: string, action: CreatorPaymentAction, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/payments/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
    linkPayout(id: string, payoutId: string) {
      return client.post(
        `${BASE}/payments/${id}/link-payout`,
        { payout_id: payoutId },
        newIdempotencyKey(),
      );
    },
  };
}

export type CreatorsApi = ReturnType<typeof createCreatorsApi>;
